import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import saveAs from 'file-saver';
import { from } from 'rxjs';
import { LoadingMask } from 'src/app/classes/loading-mask';
import { ToastService } from 'src/app/services/toast.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import {
  CompaniesApVendorMetaBulkResponseDto,
  CompaniesApVendorMetaBulkResultDto
} from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

/** One parsed CSV entry, carrying the line it came from for error messages. */
export interface ClientManagedUploadEntry {
  vendorKey: string;
  line: number;
}

/** A repeat of an earlier entry. Never sent to the API, only displayed. */
export interface ClientManagedUploadDuplicate extends ClientManagedUploadEntry {
  repeatsLine: number;
}

type ServerOutcome = CompaniesApVendorMetaBulkResultDto['outcome'];

/**
 * Rendered row. `DUPLICATE_IN_FILE` is local-only: the API collapses repeats
 * before classifying, and could not name a CSV line anyway.
 */
export type RowOutcome = ServerOutcome | 'DUPLICATE_IN_FILE';

export interface ClientManagedUploadRow {
  vendorKey: string;
  legalName: string | null;
  dba: string | null;
  outcome: RowOutcome;
  line: number;
  reason: string | null;
}

const SKIPPED_OUTCOMES: RowOutcome[] = [
  'ALREADY_CLIENT_MANAGED',
  'SKIPPED_LINKED'
];
const FAILED_OUTCOMES: RowOutcome[] = ['NOT_FOUND', 'DUPLICATE_IN_FILE'];

@Component({
  selector: 'app-ap-vendor-client-managed-upload-modal',
  templateUrl: './ap-vendor-client-managed-upload-modal.component.html',
  styleUrl: './ap-vendor-client-managed-upload-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ApVendorClientManagedUploadModalComponent implements OnInit {
  constructor(
    private activeModal: NgbActiveModal,
    private ToastService: ToastService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {}

  @Input() CompanyId?: number;
  @Input() ApConfigId?: number;
  @Input() ApConfigLabel?: string;
  @Input() FileName?: string;
  /** Distinct entries, in file order, aligned with the dry-run results. */
  @Input() Entries?: ClientManagedUploadEntry[];
  /** Repeats held back from the request so results stay aligned with Entries. */
  @Input() Duplicates?: ClientManagedUploadDuplicate[];
  @Input() DryRunResult?: CompaniesApVendorMetaBulkResponseDto;

  title: string = 'Client Managed AP Vendors Upload';
  rows: ClientManagedUploadRow[] = [];
  isApplied: boolean = false;
  appliedAt: Date | null = null;

  scanned: number = 0;
  applicableCount: number = 0;
  skippedCount: number = 0;
  failedCount: number = 0;

  close(): void {
    this.activeModal.dismiss();
  }

  isFailedRow(row: ClientManagedUploadRow): boolean {
    return FAILED_OUTCOMES.includes(row.outcome);
  }

  isSkippedRow(row: ClientManagedUploadRow): boolean {
    return SKIPPED_OUTCOMES.includes(row.outcome);
  }

  outcomeLabel(row: ClientManagedUploadRow): string {
    switch (row.outcome) {
      case 'APPLIED':
        return this.isApplied ? 'Applied' : 'Will Apply';
      case 'ALREADY_CLIENT_MANAGED':
        return 'Already Client Managed';
      case 'SKIPPED_LINKED':
        return 'Skipped — Linked';
      case 'NOT_FOUND':
        return 'Not Found';
      case 'DUPLICATE_IN_FILE':
        return 'Duplicate In File';
      default:
        return row.outcome;
    }
  }

  outcomeChipClass(row: ClientManagedUploadRow): string {
    if (this.isFailedRow(row)) {
      return 'chip chip-error';
    }

    if (this.isSkippedRow(row)) {
      return 'chip chip-warning';
    }

    return 'chip chip-success';
  }

  removeFailedRows(): void {
    const removed = this.failedCount;
    this.rows = this.rows.filter((row) => !this.isFailedRow(row));
    this.recalculate();
    this.announceRemoval(removed, 'that would fail');
  }

  removeSkippedRows(): void {
    const removed = this.skippedCount;
    this.rows = this.rows.filter((row) => !this.isSkippedRow(row));
    this.recalculate();
    this.announceRemoval(removed, 'that would be skipped');
  }

  apply(): void {
    if (!this.CompanyId || !this.ApConfigId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot apply. Missing Company ID or AP configuration.',
        { returnError: true }
      );
    }

    const vendorKeys = this.rows
      .filter((row) => row.outcome === 'APPLIED')
      .map((row) => row.vendorKey);

    if (vendorKeys.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Nothing to apply. Every remaining row is already Client Managed, already linked, or unusable.',
        { returnError: true }
      );
    }

    const mask = new LoadingMask(document.body, {
      maskText: 'Marking vendors Client Managed...'
    });

    from(
      companiesService.bulkCreateApVendorMeta({
        pathParams: { companyId: this.CompanyId, apConfigId: this.ApConfigId },
        data: { vendorKeys },
        options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
      })
    ).subscribe({
      next: (result: CompaniesApVendorMetaBulkResponseDto) => {
        mask.destroy();
        this.isApplied = true;
        this.appliedAt = new Date();
        // Duplicates were never submitted, so they are not in this response and
        // are dropped from the committed view rather than shown as pending.
        this.rows = this.buildServerRows(result);
        this.recalculate();
        this.ToastService.addSuccessToast(
          'Success',
          `${result.applied} AP vendors marked Client Managed.`
        );
        this.ChangeDetectorRef.markForCheck();
      },
      error: () => mask.destroy()
    });
  }

  exportResults(): void {
    const header = 'AP ID,Legal name,DBA,Outcome,Reason';
    const body = this.rows.map((row) =>
      [
        row.vendorKey,
        row.legalName ?? '',
        row.dba ?? '',
        this.outcomeLabel(row),
        row.reason ?? ''
      ]
        .map((value) => `"${String(value).replace(/"/g, '""')}"`)
        .join(',')
    );

    saveAs(
      new Blob([[header, ...body].join('\n')], {
        type: 'text/csv;charset=utf-8'
      }),
      'ClientManagedApVendorUploadResults.csv'
    );
  }

  private announceRemoval(removed: number, description: string): void {
    if (removed > 0) {
      this.ToastService.addSuccessToast(
        'Success',
        `Removed ${removed} row${removed !== 1 ? 's' : ''} ${description}.`
      );
    }

    this.ChangeDetectorRef.markForCheck();
  }

  private buildServerRows(
    result: CompaniesApVendorMetaBulkResponseDto
  ): ClientManagedUploadRow[] {
    const entries = this.Entries ?? [];

    return result.results.map((apiResult, index) => ({
      vendorKey: apiResult.vendorKey,
      legalName: apiResult.legalName,
      dba: apiResult.dba,
      outcome: apiResult.outcome,
      line: entries[index]?.line ?? index + 2,
      reason:
        apiResult.outcome === 'ALREADY_CLIENT_MANAGED' && apiResult.markedAt
          ? `Marked on ${new Date(apiResult.markedAt).toLocaleDateString()}`
          : apiResult.detail
    }));
  }

  private recalculate(): void {
    this.scanned = this.rows.length;
    this.applicableCount = this.rows.filter(
      (row) => row.outcome === 'APPLIED'
    ).length;
    this.skippedCount = this.rows.filter((row) =>
      this.isSkippedRow(row)
    ).length;
    this.failedCount = this.rows.filter((row) => this.isFailedRow(row)).length;
  }

  ngOnInit(): void {
    if (!this.DryRunResult) {
      this.activeModal.dismiss();
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot preview upload. No results returned.',
        { returnError: true }
      );
    }

    const duplicateRows: ClientManagedUploadRow[] = (this.Duplicates ?? []).map(
      (duplicate) => ({
        vendorKey: duplicate.vendorKey,
        legalName: null,
        dba: null,
        outcome: 'DUPLICATE_IN_FILE',
        line: duplicate.line,
        reason: `Row ${duplicate.line} repeats row ${duplicate.repeatsLine}`
      })
    );

    // Sorted by line so the table reads in the order of the uploaded file.
    this.rows = [
      ...this.buildServerRows(this.DryRunResult),
      ...duplicateRows
    ].sort((a, b) => a.line - b.line);

    this.recalculate();
  }
}
